import {TextInput} from 'platform/components';
import {Box, Center, HStack} from 'platform/foundation';
import styled from 'styled-components';

import {useState} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {normalizeHexColor} from '../utils/normalizeHexColor';

interface ColorFieldProps extends TestIdProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
}

// The swatch box has the height of the text input (8 steps), so the swatch centres on the input
// and not on the label above it.
const INPUT_HEIGHT = 8;

// A label wraps the native colour input: a click on the swatch opens the browser colour picker.
const Swatch = styled.label.attrs<{$color: string}>((props) => ({
  style: {backgroundColor: props.$color},
}))<{$color: string}>`
  position: relative;
  display: block;
  width: ${(props) => props.theme.getSize(6)};
  height: ${(props) => props.theme.getSize(6)};
  border-radius: ${(props) => props.theme.radii.small};
  border: 1px solid ${(props) => props.theme.colors.general.separator};
  overflow: hidden;
  cursor: pointer;
  user-select: none;
`;

// Visually hidden, but kept in the layout (not display: none), so the browser opens the picker
// next to the swatch.
const NativeColorInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
`;

/**
 * Hex color input (#RRGGBB). Keeps the raw text while typing and only propagates a value once it
 * parses; the committed value is upper-cased so the scene stores one spelling per color. A click on
 * the swatch opens the native colour picker, which writes through the same normalization.
 */
export function ColorField(props: ColorFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);

  const handleChange = (value: string | null) => {
    const normalized = normalizeHexColor(value ?? '');
    if (isNil(normalized)) {
      setDraft(value ?? '');
      return;
    }
    setDraft(null);
    props.onChange(normalized);
  };

  return (
    <HStack spacing={2} align="flex-end">
      <Box flex={1}>
        <TextInput
          label={props.label}
          value={draft ?? props.value}
          onChange={handleChange}
          onBlur={() => setDraft(null)}
          isInvalid={!isNil(draft)}
          maxLength={7}
          data-testid={suffixTestId('input', props)}
        />
      </Box>
      <Center height={INPUT_HEIGHT}>
        <Swatch $color={props.value} data-testid={suffixTestId('swatch', props)}>
          <NativeColorInput
            type="color"
            aria-label={i18n.t('entity.watermark.composition.labels.pickColor', {
              label: props.label,
            })}
            value={props.value.toLowerCase()}
            onChange={(event) => handleChange(event.target.value)}
            data-testid={suffixTestId('picker', props)}
          />
        </Swatch>
      </Center>
    </HStack>
  );
}
